import type { Office } from "@homeinstead/schemas";

type FooterSocialLinks = {
  facebook?: string;
  tiktok?: string;
  instagram?: string;
  linkedin?: string;
  youtube?: string;
};

type OfficeWithSocialUrls = Office & {
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  tiktokUrl?: string | null;
  linkedinUrl?: string | null;
  youtubeUrl?: string | null;
};

/**
 * Maps office social media links to the format expected by FooterSocialIcons
 *
 * The office object is flattened from Sanity GROQ queries, which extract
 * individual platform URLs to root-level properties (e.g., facebookUrl, instagramUrl).
 *
 * @param office - Office document with root-level social media URL properties
 * @returns Object with social media links keyed by platform name
 */
export function mapOfficeSocialMediaLinksToFooterLinks(
  office?: Office,
): FooterSocialLinks {
  const links: FooterSocialLinks = {};

  if (!office) {
    return links;
  }

  const officeWithUrls = office as OfficeWithSocialUrls;

  // Map of platform keys to their root-level URL property names
  const rootUrlMap: Record<
    keyof FooterSocialLinks,
    keyof OfficeWithSocialUrls
  > = {
    facebook: "facebookUrl",
    instagram: "instagramUrl",
    linkedin: "linkedinUrl",
    tiktok: "tiktokUrl",
    youtube: "youtubeUrl",
  };

  // Check for root-level URLs (extracted by GROQ queries)
  // The office object is flattened from Sanity, so we expect root-level URL properties
  for (const [platformKey, urlProperty] of Object.entries(rootUrlMap)) {
    const url = officeWithUrls[urlProperty];
    if (url && typeof url === "string") {
      links[platformKey as keyof FooterSocialLinks] = url;
    }
  }

  return links;
}
